import React, { useMemo } from 'react';
import { Box, Text, HStack, VStack } from '@chakra-ui/react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Sphere,
  Graticule,
} from 'react-simple-maps';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const CITY_COORDS = {
  'dallas, tx': [-96.797, 32.777],
  'austin, tx': [-97.743, 30.267],
  'houston, tx': [-95.370, 29.760],
  'san antonio, tx': [-98.494, 29.424],
  'phoenix, az': [-112.074, 33.449],
  'los angeles, ca': [-118.244, 34.052],
  'san francisco, ca': [-122.419, 37.775],
  'san diego, ca': [-117.161, 32.716],
  'denver, co': [-104.990, 39.739],
  'chicago, il': [-87.630, 41.878],
  'new york, ny': [-74.006, 40.713],
  'miami, fl': [-80.192, 25.762],
  'atlanta, ga': [-84.388, 33.749],
  'seattle, wa': [-122.332, 47.606],
  'portland, or': [-122.676, 45.523],
  'nashville, tn': [-86.784, 36.163],
  'charlotte, nc': [-80.843, 35.227],
  'boston, ma': [-71.059, 42.360],
  'detroit, mi': [-83.046, 42.331],
  'minneapolis, mn': [-93.265, 44.978],
  'kansas city, mo': [-94.579, 39.100],
  'las vegas, nv': [-115.140, 36.175],
  'salt lake city, ut': [-111.891, 40.761],
  'tampa, fl': [-82.458, 27.951],
  'orlando, fl': [-81.379, 28.538],
  'raleigh, nc': [-78.639, 35.780],
  'columbus, oh': [-82.999, 39.961],
  'indianapolis, in': [-86.158, 39.769],
  'oklahoma city, ok': [-97.517, 35.468],
  'fort worth, tx': [-97.331, 32.749],
  'jacksonville, fl': [-81.656, 30.332],
  'san jose, ca': [-121.886, 37.338],
  'memphis, tn': [-90.049, 35.150],
  'louisville, ky': [-85.759, 38.253],
  'baltimore, md': [-76.612, 39.290],
  'milwaukee, wi': [-87.907, 43.039],
  'albuquerque, nm': [-106.651, 35.085],
  'tucson, az': [-110.926, 32.222],
  'fresno, ca': [-119.773, 36.747],
  'sacramento, ca': [-121.494, 38.582],
  'mesa, az': [-111.831, 33.415],
  'omaha, ne': [-95.938, 41.257],
  'colorado springs, co': [-104.821, 38.834],
  'virginia beach, va': [-75.978, 36.853],
  'new orleans, la': [-90.075, 29.951],
  'pittsburgh, pa': [-79.990, 40.441],
  'st. louis, mo': [-90.199, 38.627],
  'cincinnati, oh': [-84.512, 39.103],
  'cleveland, oh': [-81.694, 41.500],
  'st. paul, mn': [-93.090, 44.954],
  'knoxville, tn': [-83.921, 35.961],
  'chattanooga, tn': [-85.310, 35.046],
  'birmingham, al': [-86.802, 33.521],
  'richmond, va': [-77.436, 37.541],
  'boise, id': [-116.203, 43.615],
  'spokane, wa': [-117.426, 47.659],
  'charleston, sc': [-79.931, 32.777],
  'savannah, ga': [-81.100, 32.081],
  'greenville, sc': [-82.394, 34.852],
  'columbia, sc': [-81.035, 34.000],
  'little rock, ar': [-92.290, 34.746],
  'baton rouge, la': [-91.140, 30.458],
  'fort lauderdale, fl': [-80.137, 26.122],
  'grand rapids, mi': [-85.668, 42.963],
  'des moines, ia': [-93.609, 41.587],
  'huntsville, al': [-86.586, 34.730],
  'el paso, tx': [-106.425, 31.761],
  'waco, tx': [-97.147, 31.549],
  'lubbock, tx': [-101.845, 33.578],
  'amarillo, tx': [-101.831, 35.222],
  'corpus christi, tx': [-97.396, 27.801],
  'plano, tx': [-96.699, 33.020],
  'arlington, tx': [-97.108, 32.736],
  'toronto, on': [-79.383, 43.653],
  'vancouver, bc': [-123.121, 49.283],
  'montreal, qc': [-73.568, 45.502],
  'calgary, ab': [-114.071, 51.045],
  'edmonton, ab': [-113.491, 53.546],
  'ottawa, on': [-75.698, 45.421],
  'mexico city': [-99.133, 19.433],
};

const GLOW_PALETTE = [
  { main: '#00e5ff', glow: 'rgba(0,229,255,0.7)', ring: 'rgba(0,229,255,0.25)' },
  { main: '#a855f7', glow: 'rgba(168,85,247,0.7)', ring: 'rgba(168,85,247,0.25)' },
  { main: '#10b981', glow: 'rgba(16,185,129,0.7)', ring: 'rgba(16,185,129,0.25)' },
  { main: '#3b82f6', glow: 'rgba(59,130,246,0.7)', ring: 'rgba(59,130,246,0.25)' },
  { main: '#2dd4bf', glow: 'rgba(45,212,191,0.7)', ring: 'rgba(45,212,191,0.25)' },
  { main: '#818cf8', glow: 'rgba(129,140,248,0.7)', ring: 'rgba(129,140,248,0.25)' },
  { main: '#22c55e', glow: 'rgba(34,197,94,0.7)', ring: 'rgba(34,197,94,0.25)' },
  { main: '#14b8a6', glow: 'rgba(20,184,166,0.7)', ring: 'rgba(20,184,166,0.25)' },
  { main: '#6366f1', glow: 'rgba(99,102,241,0.7)', ring: 'rgba(99,102,241,0.25)' },
  { main: '#34d399', glow: 'rgba(52,211,153,0.7)', ring: 'rgba(52,211,153,0.25)' },
];

function resolveCoords(scrape) {
  if (scrape.lat != null && scrape.lng != null) {
    return [scrape.lng, scrape.lat];
  }

  const region = scrape.region;
  if (region) {
    const key = region.toLowerCase().trim();
    if (CITY_COORDS[key]) return CITY_COORDS[key];
    for (const [k, v] of Object.entries(CITY_COORDS)) {
      if (key.includes(k) || k.includes(key)) return v;
    }
  }

  // Fallback: try to extract a city name from the scrape id, name, or db_name.
  // Vertical names often contain city info, e.g. "plumbers-dallas" or
  // "hvac_austin_tx". Also handles abbreviated forms like "Nash" for Nashville.
  const raw = (scrape.id || scrape.db_name || scrape.name || '').toLowerCase().replace(/[-_]/g, ' ');
  if (raw) {
    // First pass: exact city name match (e.g. "dallas" in "plumbers dallas")
    for (const [k, v] of Object.entries(CITY_COORDS)) {
      const city = k.split(',')[0].trim();
      if (city && raw.includes(city)) return v;
    }
    // Second pass: check if a city name starts with any word in the scrape name
    // (e.g. "nash" matches "nashville"). Only for tokens >= 4 chars to avoid
    // false positives.
    const tokens = raw.split(/\s+/).filter(t => t.length >= 4);
    if (tokens.length > 0) {
      for (const [k, v] of Object.entries(CITY_COORDS)) {
        const city = k.split(',')[0].trim();
        if (city && tokens.some(t => city.startsWith(t))) return v;
      }
    }
  }

  return null;
}

function ScrapeMapPanel({ scrapes }) {
  const markers = useMemo(() => {
    let colorIdx = 0;

    return scrapes
      .map((s) => {
        const coords = resolveCoords(s);
        if (!coords) return null;

        const palette = GLOW_PALETTE[colorIdx % GLOW_PALETTE.length];
        colorIdx++;

        const scrapeId = s.id || s.db_name || s.name || `scrape-${colorIdx}`;
        return {
          id: scrapeId,
          name: s.name || s.id || scrapeId,
          region: s.region,
          niche: s.niche || 'Other',
          coords,
          palette,
          businessCount: s.businessCount,
        };
      })
      .filter(Boolean);
  }, [scrapes]);

  const legend = useMemo(() => {
    const seen = {};
    markers.forEach((m) => {
      if (!seen[m.niche]) seen[m.niche] = m.palette;
    });
    return Object.entries(seen);
  }, [markers]);

  return (
    <Box
      bg="#080c14"
      borderRadius="20px"
      border="1px solid"
      borderColor="whiteAlpha.100"
      overflow="hidden"
      position="relative"
      h="100%"
    >
      {/* Atmosphere glow behind the globe */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -45%)"
        w="85%"
        h="75%"
        borderRadius="full"
        bg="transparent"
        boxShadow="0 0 80px 20px rgba(56,128,255,0.08), 0 0 160px 60px rgba(56,128,255,0.04)"
        pointerEvents="none"
        zIndex={0}
      />

      <VStack
        position="relative"
        zIndex={1}
        align="stretch"
        h="100%"
        spacing={0}
      >
        <Box px={5} pt={4} pb={1}>
          <Text
            fontSize="xs"
            fontWeight="700"
            color="whiteAlpha.400"
            textTransform="uppercase"
            letterSpacing="0.1em"
          >
            Market Report Locations
          </Text>
        </Box>

        <Box flex={1} position="relative" overflow="hidden">
          <ComposableMap
            projection="geoOrthographic"
            projectionConfig={{
              rotate: [98, -38, 0],
              scale: 520,
            }}
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
            width={560}
            height={560}
          >
            <defs>
              {GLOW_PALETTE.map((p, i) => (
                <filter key={i} id={`glow-${i}`} x="-100%" y="-100%" width="300%" height="300%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              ))}
              <radialGradient id="globe-gradient" cx="40%" cy="35%" r="60%">
                <stop offset="0%" stopColor="#1e3a5f" />
                <stop offset="100%" stopColor="#0a1628" />
              </radialGradient>
              <radialGradient id="ocean-gradient" cx="45%" cy="40%" r="65%">
                <stop offset="0%" stopColor="#0f1d32" />
                <stop offset="100%" stopColor="#060c18" />
              </radialGradient>
              <radialGradient id="atmosphere" cx="50%" cy="50%" r="50%">
                <stop offset="85%" stopColor="transparent" />
                <stop offset="100%" stopColor="rgba(100,180,255,0.08)" />
              </radialGradient>
            </defs>

            <Sphere
              stroke="rgba(100,160,255,0.15)"
              strokeWidth={1.5}
              fill="url(#ocean-gradient)"
            />

            <Graticule
              stroke="rgba(100,160,255,0.06)"
              strokeWidth={0.4}
            />

            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#1a2d45"
                    stroke="#2a4a6b"
                    strokeWidth={0.6}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', fill: '#223a56' },
                      pressed: { outline: 'none' },
                    }}
                  />
                ))
              }
            </Geographies>

            {/* Atmosphere overlay */}
            <Sphere fill="url(#atmosphere)" stroke="none" />

            {markers.map((m) => {
              const pIdx = GLOW_PALETTE.indexOf(m.palette);
              return (
                <Marker key={m.id} coordinates={m.coords}>
                  <circle
                    r={28}
                    fill={m.palette.ring}
                    style={{ animation: 'pulse-ring 3s ease-out infinite' }}
                  />
                  <circle
                    r={16}
                    fill={m.palette.ring}
                  />
                  <circle
                    r={8}
                    fill={m.palette.main}
                    filter={`url(#glow-${pIdx >= 0 ? pIdx : 0})`}
                    style={{ cursor: 'pointer' }}
                  />
                  <circle
                    r={3}
                    fill="white"
                    opacity={0.8}
                  />
                  <text
                    textAnchor="middle"
                    y={-22}
                    style={{
                      fontFamily: 'inherit',
                      fontSize: '11px',
                      fontWeight: 700,
                      fill: m.palette.main,
                      textShadow: `0 0 10px ${m.palette.glow}, 0 1px 3px rgba(0,0,0,0.8)`,
                    }}
                  >
                    {m.name}
                  </text>
                  <text
                    textAnchor="middle"
                    y={-10}
                    style={{
                      fontFamily: 'inherit',
                      fontSize: '8.5px',
                      fontWeight: 500,
                      fill: 'rgba(255,255,255,0.55)',
                      textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                    }}
                  >
                    {m.region}
                  </text>
                </Marker>
              );
            })}
          </ComposableMap>

          <style>{`
            @keyframes pulse-ring {
              0% { opacity: 0.7; r: 14; }
              70% { opacity: 0; r: 34; }
              100% { opacity: 0; r: 34; }
            }
          `}</style>
        </Box>

        {legend.length > 0 && (
          <HStack
            px={5}
            pb={4}
            pt={1}
            spacing={4}
            flexWrap="wrap"
            justify="center"
          >
            {legend.map(([niche, palette]) => (
              <HStack key={niche} spacing={2}>
                <Box
                  w="10px"
                  h="10px"
                  borderRadius="full"
                  bg={palette.main}
                  boxShadow={`0 0 8px ${palette.glow}`}
                />
                <Text
                  fontSize="11px"
                  fontWeight="600"
                  color="whiteAlpha.600"
                  letterSpacing="0.02em"
                >
                  {niche}
                </Text>
              </HStack>
            ))}
          </HStack>
        )}
      </VStack>
    </Box>
  );
}

export default ScrapeMapPanel;
